import { Step, Field, StepRoutingRules } from "@lib/shared/types";
import { FieldsData } from "../../../../../hooks/useForm/types";

type FieldStepRouting = {
    isActive: boolean;
    rules: StepRoutingRules[];
};

function getFieldStepRouting(field: Field): FieldStepRouting | null {
    const rawMetaRouting = field.meta?.stepRouting;
    if (!rawMetaRouting) {
        return null;
    }

    try {
        const parsed = JSON.parse(rawMetaRouting) as FieldStepRouting;
        if (
            typeof parsed?.isActive === "boolean" &&
            Array.isArray(parsed?.rules)
        ) {
            return parsed;
        }
    } catch {
        return null;
    }

    return null;
}

function evaluateRule(
    rule: StepRoutingRules,
    fieldValue: string | undefined,
): boolean {
    const ruleValue = String(rule.value ?? "");
    const actual = String(fieldValue ?? "");

    switch (rule.operator) {
        case "is":
            return actual === ruleValue;
        case "isNot":
            return actual !== ruleValue;
        case "contains":
            return actual.includes(ruleValue);
        case "doesNotContain":
            return !actual.includes(ruleValue);
        case "greaterThan":
            return Number(actual) > Number(ruleValue);
        case "lessThan":
            return Number(actual) < Number(ruleValue);
        default:
            return actual === ruleValue;
    }
}

/**
 * Checks if the current step has fields with active stepRouting rules
 * that match the current field values. If so, returns the target step index.
 * Returns null if no routing applies.
 */
export function getRoutedStepIndex(
    steps: Step[],
    currentStep: number,
    fieldsData: FieldsData,
): number | null {
    const currentFields: Field[] = steps[currentStep]?.fields ?? [];

    for (const field of currentFields) {
        const stepRouting = getFieldStepRouting(field);
        if (!stepRouting?.isActive || !stepRouting?.rules?.length) {
            continue;
        }

        const fieldValue = fieldsData[field.codeName]?.value;

        for (const rule of stepRouting.rules) {
            if (evaluateRule(rule, fieldValue)) {
                const targetStepIndex = steps.findIndex(
                    (step) => step.id === Number(rule.targetStep),
                );
                if (targetStepIndex !== -1) {
                    return targetStepIndex;
                }
            }
        }
    }

    return null;
}

/**
 * Computes the set of step indices that should be skipped based on the
 * current answers and active stepRouting rules.
 *
 * For each step `i` whose field has an active routing rule that matches the
 * current value, the indices strictly between `i` and the target step index
 * are added to the skipped set. Backward routing (target <= i) is ignored.
 * The first matching rule per field wins.
 */
export function getSkippedStepIndices(
    steps: Step[],
    fieldsData: FieldsData,
): Set<number> {
    const skipped = new Set<number>();

    steps.forEach((step, i) => {
        for (const field of step.fields) {
            const stepRouting = getFieldStepRouting(field);
            if (!stepRouting?.isActive || !stepRouting?.rules?.length) {
                continue;
            }

            const fieldValue = fieldsData[field.codeName]?.value;
            let matchedTarget: number | null = null;

            for (const rule of stepRouting.rules) {
                if (evaluateRule(rule, fieldValue)) {
                    const targetStepIndex = steps.findIndex(
                        (s) => s.id === Number(rule.targetStep),
                    );
                    if (targetStepIndex !== -1) {
                        matchedTarget = targetStepIndex;
                        break;
                    }
                }
            }

            if (matchedTarget !== null && matchedTarget > i) {
                for (let j = i + 1; j < matchedTarget; j++) {
                    skipped.add(j);
                }
            }
        }
    });

    return skipped;
}

/**
 * Returns the nearest non-skipped index strictly less than `from`, or 0 if
 * none is reachable.
 */
export function findPrevVisibleIndex(
    from: number,
    skipped: Set<number>,
): number {
    let idx = from - 1;
    while (idx > 0 && skipped.has(idx)) {
        idx -= 1;
    }
    return Math.max(idx, 0);
}

/**
 * Returns the nearest non-skipped index strictly greater than `from`, or the
 * last step index if none is reachable.
 */
export function findNextVisibleIndex(
    from: number,
    skipped: Set<number>,
    totalSteps: number,
): number {
    let idx = from + 1;
    while (idx < totalSteps - 1 && skipped.has(idx)) {
        idx += 1;
    }
    return Math.min(idx, totalSteps - 1);
}
